import { appendFileSync, existsSync, readFileSync, writeFileSync } from "node:fs";
import { join } from "node:path";
// Step 2 + 3 của Phase 10 GitNexus integration:
//   - runGitnexusSetup()   : `gitnexus setup` → install 4-6 core skills vào ~/.claude/skills/gitnexus-*
//   - runGitnexusAnalyze() : `gitnexus analyze .` ở workspace root → tạo .gitnexus/ với index + meta.json
//
// Cả 2 stream stdio để user thấy progress (long-running commands).
// Throws GitnexusOperationError với operation + reason để caller (orchestrator) recovery menu.
import { spawnAsyncCapture } from "./spawn-async-capture-with-timeout.js";
import { spinnerWithElapsed } from "./terminal-logger.js";

// Timeouts theo doc GitNexus performance estimate.
const SETUP_TIMEOUT_MS = 2 * 60 * 1000; // 2 phút — gitnexus setup nhanh (chỉ copy skill files)
const ANALYZE_TIMEOUT_MS = 5 * 60 * 1000; // 5 phút — analyze repo medium

export type GitnexusOperation = "setup" | "analyze";
export type GitnexusOperationReason =
  | "binary-not-found" // gitnexus không trong PATH (sanity check)
  | "timeout" // SIGTERM
  | "permission" // EACCES khi write file
  | "non-zero-exit" // generic failure
  | "missing-output"; // analyze xong nhưng .gitnexus/meta.json không tạo

export class GitnexusOperationError extends Error {
  operation: GitnexusOperation;
  reason: GitnexusOperationReason;
  exitCode: number | null;
  stderr?: string;
  constructor(
    operation: GitnexusOperation,
    reason: GitnexusOperationReason,
    message: string,
    exitCode: number | null = null,
    stderr?: string,
  ) {
    super(message);
    this.name = "GitnexusOperationError";
    this.operation = operation;
    this.reason = reason;
    this.exitCode = exitCode;
    this.stderr = stderr;
  }
}

// Classify exit code + stderr thành reason chuẩn hóa cho recovery menu.
function classifyOperationFailure(
  operation: GitnexusOperation,
  exitCode: number | null,
  signal: string | null,
  stderrSample: string,
): GitnexusOperationError {
  if (signal === "SIGTERM") {
    return new GitnexusOperationError(
      operation,
      "timeout",
      `gitnexus ${operation} timeout. Check mạng / repo size.`,
      null,
      stderrSample,
    );
  }
  const stderr = stderrSample.toLowerCase();
  if (stderr.includes("eacces") || stderr.includes("permission denied")) {
    return new GitnexusOperationError(
      operation,
      "permission",
      `gitnexus ${operation} fail (permission). Check write access ~/.claude hoặc cwd.`,
      exitCode,
      stderrSample,
    );
  }
  return new GitnexusOperationError(
    operation,
    "non-zero-exit",
    `gitnexus ${operation} exit ${exitCode ?? "null"}. Xem log phía trên.`,
    exitCode,
    stderrSample,
  );
}

// Tail N dòng cuối — dùng khi fail để debug mà không spam terminal.
function tailLines(text: string, n: number): string {
  const lines = text.split("\n");
  return lines.slice(-n).join("\n");
}

// Step 2 — Run `gitnexus setup` để install global skills (~/.claude/skills/gitnexus-*).
// Idempotent: chạy lần 2 OK, GitNexus tự skip nếu skills đã có.
// Spinner với elapsed time + buffer stdio (không inherit) để spinner mượt.
export async function runGitnexusSetup(): Promise<void> {
  const sp = spinnerWithElapsed("Setup GitNexus global skills (~/.claude/skills/gitnexus-*)");
  // spawn async: spinner tick được + Ctrl-C kill child (xem spawn-async-capture).
  const result = await spawnAsyncCapture("gitnexus", ["setup"], {
    timeoutMs: SETUP_TIMEOUT_MS,
  });

  if (result.status !== 0 || result.timedOut) {
    sp.fail("GitNexus setup failed");
    const stderr = result.stderr.trim();
    const stdout = result.stdout.trim();
    if (stderr) process.stderr.write(`${tailLines(stderr, 30)}\n`);
    else if (stdout) process.stderr.write(`${tailLines(stdout, 30)}\n`);
    throw classifyOperationFailure(
      "setup",
      result.status,
      result.timedOut ? "SIGTERM" : null,
      stderr,
    );
  }
  sp.succeed("GitNexus setup OK (global skills installed)");
}

// Step 3 — Run `gitnexus analyze . --index-only` trong repo.
//
// v3 --index-only: PURE INDEX MODE — gitnexus CHỈ tạo .gitnexus/ (database), KHÔNG:
//   - tạo .claude/skills/gitnexus/ (skill này ĐÃ có ở ~/.claude/skills/ global — copy thừa)
//   - sửa AGENTS.md / CLAUDE.md của repo khách (ô nhiễm file khách)
// → repo khách KHÔNG bị thêm .claude, KHÔNG bị sửa file. Chỉ còn .gitnexus/ (database).
//
// Sau analyze: tự thêm .gitnexus/ vào .gitignore của repo → database 38MB KHÔNG
// commit/push lên repo khách (vẫn dùng được local cho Claude query qua MCP).
export async function runGitnexusAnalyze(workspacePath: string): Promise<void> {
  const sp = spinnerWithElapsed(`Analyze ${workspacePath} (1-3 phút)`);
  const result = await spawnAsyncCapture("gitnexus", ["analyze", ".", "--index-only"], {
    cwd: workspacePath,
    timeoutMs: ANALYZE_TIMEOUT_MS,
  });

  if (result.status !== 0 || result.timedOut) {
    sp.fail("Analyze failed");
    const stderr = result.stderr.trim();
    const stdout = result.stdout.trim();
    if (stderr) process.stderr.write(`${tailLines(stderr, 30)}\n`);
    else if (stdout) process.stderr.write(`${tailLines(stdout, 30)}\n`);
    throw classifyOperationFailure(
      "analyze",
      result.status,
      result.timedOut ? "SIGTERM" : null,
      stderr,
    );
  }

  // Verify output tồn tại — analyze có thể exit 0 nhưng không tạo meta.json (edge case).
  const metaPath = join(workspacePath, ".gitnexus", "meta.json");
  if (!existsSync(metaPath)) {
    sp.fail("Analyze exit 0 nhưng không thấy meta.json");
    throw new GitnexusOperationError(
      "analyze",
      "missing-output",
      `gitnexus analyze xong nhưng không thấy ${metaPath}. Repo có thể empty hoặc gitnexus fail silent.`,
    );
  }

  // Đảm bảo .gitnexus/ được gitignore trong repo → database KHÔNG commit/push lên repo khách.
  ensureGitnexusGitignored(workspacePath);

  sp.succeed(`Analyze OK (index tại ${join(workspacePath, ".gitnexus")}, đã gitignore)`);
}

// Thêm `.gitnexus/` vào .gitignore của repo (idempotent) — database local, không track.
function ensureGitnexusGitignored(repoPath: string): void {
  const gitignorePath = join(repoPath, ".gitignore");
  const entry = ".gitnexus/";
  try {
    let content = "";
    if (existsSync(gitignorePath)) {
      content = readFileSync(gitignorePath, "utf8");
      // Đã có entry (chính xác hoặc biến thể) → skip.
      if (/^\.gitnexus\/?\s*$/m.test(content)) return;
    }
    const prefix = content.length > 0 && !content.endsWith("\n") ? "\n" : "";
    const block = `${prefix}\n# GitNexus index (Avatar) — database local, không commit.\n${entry}\n`;
    if (content.length > 0) {
      appendFileSync(gitignorePath, block);
    } else {
      writeFileSync(
        gitignorePath,
        `# GitNexus index (Avatar) — database local, không commit.\n${entry}\n`,
      );
    }
  } catch {
    // Best-effort: fail gitignore không block analyze (index vẫn dùng được local).
  }
}
